/**
 * Demo-only workflow definitions — the IDW lanes, for `/demo/applicant-flow`.
 *
 * ## Why this exists
 *
 * `ApplicantFlow` renders a definition-driven plan (ADR-014) only when the
 * session carries a `workflow_key`. Demo mode has no backend and therefore no
 * session, so the demo could only ever render `LEGACY_PLAN` — the Rockwell /
 * leasing journey. That made the two IDW lanes undemoable without a real
 * inquiry and a real token.
 *
 * These are the same two definitions the platform seeds for the `IDW` org,
 * transcribed so the demo renders the plan the back end would have sent.
 *
 * ## Keep in step with the seed
 *
 * The step lists below are the fixtures already pinned by
 * `src/lib/__tests__/flowPlan.test.ts`, which is itself pinned against the
 * seeded definitions. If a migration changes a lane, both move together — a
 * demo that quietly drifts from the real flow is worse than no demo, because
 * it is the version people in the room believe.
 *
 * ⚠️ Both lanes are at v3 and both are DISJOINT — see the note on the satisfier
 * sets below. The lanes differ in exactly ONE dimension, which is the point of
 * showing them side by side: `tech_role` requires the digital account and adds
 * an ID check; `high_volume` does neither. Do not introduce a second
 * difference.
 */
import type { WorkflowStep } from "@/lib/api";
import type { Vertical } from "@/lib/verticalCopy";
import { MICROSOFT_EMAIL_PROOF_STEP } from "@/lib/emailProof";

/**
 * Both lanes are at **v3**, and both are DISJOINT.
 *
 * Transcribed from the platform seed migrations, which are the source of truth:
 *
 *   1787461200000_seed-idw-demo-org.js            (v1, both lanes)
 *   1787634000000_idw-tech-role-v2-disjoint-satisfiers.js
 *   1787720400000_idw-high-volume-v2-disjoint-satisfiers.js
 *   1787806800000_idw-tech-role-v3-facebook-satisfier.js
 *   1787893200000_idw-high-volume-v3-facebook-satisfier.js
 *
 * ⚠️ Do NOT take these from `flowPlan.test.ts`. Its fixtures still describe
 * `high_volume` at v1 with OVERLAPPING sets, and `tech_role` at v2 without
 * Facebook — both superseded in the platform. The v2 migration reversed the
 * "overlap is deliberate here" position outright: with `social` optional the
 * overlap gated nothing, but it made `getSessionProgress` read 3/3 when the
 * applicant had connected one Google account and never touched the
 * digital-account step. A progress number claiming something the applicant did
 * not do is the thing that made it worth a migration.
 *
 * v3 then added `oauth_facebook` to the digital-account step on both lanes,
 * because #424 made Facebook a real OAuth provider that writes a step. It
 * cannot satisfy `email` — Facebook establishes no verified address — which is
 * what keeps the two sets disjoint.
 *
 * OTP satisfies `email` without being named: `isStepSatisfied` matches the step
 * type first, and the OTP path writes `email` directly.
 */
// v4 (platform#712): a Microsoft sign-in satisfies `email` only through the
// proof step the server records when that sign-in proved the address.
const EMAIL_ALTS = ["oauth_google", MICROSOFT_EMAIL_PROOF_STEP, "oauth_apple"];
const ACCOUNT_ALTS = ["oauth_linkedin", "oauth_amazon", "oauth_facebook"];

/**
 * The org the IDW lanes present as. One name for both: they are two journeys
 * configured by ONE org (`IDW`), not two organisations, and inventing a
 * per-lane name would imply otherwise on the consent screen.
 */
const IDW_ORG = "IDW";

/**
 * The org the demo presents as with no `?flow=` — the leasing walkthrough.
 *
 * Lives here rather than inline in `ApplicantFlow` because it used to be TWO
 * literals that disagreed: the step-0 intro said "DV Properties" while the
 * consent box said "Rockwell Properties", in the same walkthrough. Rockwell is
 * the leasing pilot and what the step-0 copy brief asked for, so that is the
 * one that survives.
 */
export const DEMO_LEASING_ORG = "Rockwell Properties";

/** The lanes a demo URL can name. `standard` is not one — it means NO key. */
export const DEMO_FLOW_KEYS = ["high_volume", "tech_role"] as const;
export type DemoFlowKey = (typeof DEMO_FLOW_KEYS)[number];

export interface DemoFlow {
  key: DemoFlowKey;
  /** Org name the synthetic session reports — recruiting, not leasing. */
  orgName: string;
  /**
   * `organizations.industry`, which is what selects the vertical copy.
   *
   * Both IDW lanes are recruiting. This is the REAL field the flow reads
   * (`verticalFrom(session.org_industry)`) — deliberately not a sixth proxy
   * for the vertical. See CLAUDE.md "Vertical-aware copy, routing and theme".
   */
  industry: Vertical;
  steps: WorkflowStep[];
}

const HIGH_VOLUME: DemoFlow = {
  key: "high_volume",
  orgName: IDW_ORG,
  industry: "hr_recruiting",
  steps: [
    { step_type: "consent", required: true },
    { step_type: "email", required: true, satisfied_by: EMAIL_ALTS },
    { step_type: "social", required: false, satisfied_by: ACCOUNT_ALTS },
  ],
};

const TECH_ROLE: DemoFlow = {
  key: "tech_role",
  orgName: IDW_ORG,
  industry: "hr_recruiting",
  steps: [
    { step_type: "consent", required: true },
    { step_type: "plaid_idv", required: true },
    { step_type: "email", required: true, satisfied_by: EMAIL_ALTS },
    { step_type: "social", required: true, satisfied_by: ACCOUNT_ALTS },
  ],
};

const BY_KEY: Record<DemoFlowKey, DemoFlow> = {
  high_volume: HIGH_VOLUME,
  tech_role: TECH_ROLE,
};

export const DEMO_FLOWS: DemoFlow[] = [HIGH_VOLUME, TECH_ROLE];

/**
 * Resolve `?flow=` to a lane, or null for the leasing demo.
 *
 * Anything unrecognised resolves to null rather than throwing, so a typo'd or
 * stale demo link degrades to the leasing flow — which is complete and
 * walkable — instead of a blank screen in front of an audience.
 */
export function demoFlowFromParam(raw: string | null | undefined): DemoFlow | null {
  if (!raw) return null;
  return BY_KEY[raw as DemoFlowKey] ?? null;
}

/**
 * The OAuth providers the demo advertises as configured, derived from the
 * lane's OWN satisfiers rather than listed a second time.
 *
 * `offerableSources` hides any `gatedOnAvailability` tile whose provider is
 * not in `session.available_oauth_providers`. The demo used to report an empty
 * list — "nothing is configured, because there is no backend" — which was
 * defensible until it started claiming to BE a lane: Facebook is a v3
 * digital-account satisfier on both lanes and it silently vanished from the
 * step the lane requires.
 *
 * Deriving it means a satisfier added to a lane cannot fail to appear as a
 * tile, which is exactly the drift that produced this.
 */
export function demoAvailableOAuth(flow: DemoFlow | null): string[] {
  if (!flow) return [];
  const alts = flow.steps.flatMap((step) => step.satisfied_by ?? []);
  return Array.from(new Set(alts))
    // The proof step records a fact about a Microsoft sign-in; it is not a
    // provider of its own.
    .filter((a) => a.startsWith("oauth_") && a !== MICROSOFT_EMAIL_PROOF_STEP)
    .map((a) => a.slice("oauth_".length));
}

/**
 * Said on the review routes (`/article/*`, `/feather/*`), whose screens use the
 * production components and so can pass for a real application. The leasing
 * demo says the same thing in its own banner.
 */
export const PREVIEW_ROUTE_NOTE = "Preview with sample data. Nothing you enter here is sent.";
